import React, { useEffect, useMemo, useState } from "react";
import { Check, Copy, MessageCircle, Phone, Send } from "lucide-react";
import { useTranslation } from "react-i18next";

// REISOR_DRIVER_CONTACT_ACTIONS_V1
// REISOR_DRIVER_TELEGRAM_REACHABLE_CONTACT_V2
// UI-only helper. It never discovers contacts and never changes access rules.
// Parents pass a phone only after the existing backend authorization has exposed it.
export function normalizeDriverContactPhone(value) {
  const raw = String(value || "").trim();
  const digits = raw.replace(/\D/g, "");
  if (digits.length < 7 || digits.length > 15) {
    return { raw, digits: "", tel: "", whatsapp: "", telegram: "", telegramApp: "" };
  }

  const tel = raw.startsWith("+") ? `+${digits}` : digits;
  return {
    raw,
    digits,
    tel,
    whatsapp: `https://wa.me/${digits}`,
    // No ?profile: Telegram documents that flag as profile-only navigation.
    // Without it the phone link resolves to the private chat when Telegram can resolve the number.
    telegram: `https://t.me/+${digits}`,
  };
}

async function copyText(value) {
  const text = String(value || "").trim();
  if (!text) return false;

  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {}

  try {
    if (typeof document === "undefined") return false;
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(area);
    return Boolean(ok);
  } catch {
    return false;
  }
}

function normalizeTelegramUsername(value) {
  const username = String(value || "").trim().replace(/^@+/, "");
  if (!/^[A-Za-z0-9_]{5,32}$/.test(username)) return "";
  return username;
}

export default function DriverContactActions({
  phone,
  telegramUsername = "",
  telegramSenderId: _telegramSenderId = null,
  allowPhoneTelegram = true,
  draftText = "",
  callLabel = "",
  className = "",
  variant = "pro",
}) {
  const { t } = useTranslation();
  const contact = useMemo(() => normalizeDriverContactPhone(phone), [phone]);
  const normalizedDraftText = useMemo(
    () => String(draftText || "").trim().slice(0, 2000),
    [draftText]
  );
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  useEffect(() => {
    setCopied(false);
    setCopyFailed(false);
  }, [contact.raw]);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(false), 1800);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const canLaunch = Boolean(contact.digits);
  const normalizedTelegramUsername = normalizeTelegramUsername(telegramUsername);

  // REISOR_FREIGHT_TELEGRAM_CONTACT_PRIORITY_V2
  // Cargo contact and REISOR phone-verification bot are separate trust domains.
  // When the freight order exposes a phone, that phone is the authoritative
  // Telegram destination. A source username is only a fallback when no phone
  // is available. The platform verification bot must never become freight contact.
  const isPlatformVerificationBot =
    normalizedTelegramUsername.toLowerCase() === "luxeaybot";

  const safeTelegramUsername =
    isPlatformVerificationBot ? "" : normalizedTelegramUsername;

  void _telegramSenderId;

  const telegramBaseHref =
    allowPhoneTelegram && canLaunch
      ? contact.telegram
      : safeTelegramUsername
        ? `https://t.me/${safeTelegramUsername}`
        : "";

  const telegramHref = useMemo(() => {
    if (!telegramBaseHref || !normalizedDraftText) return telegramBaseHref;

    try {
      const url = new URL(telegramBaseHref);
      url.searchParams.set("text", normalizedDraftText);
      return url.toString();
    } catch {
      return telegramBaseHref;
    }
  }, [telegramBaseHref, normalizedDraftText]);

  if (!contact.raw && !telegramHref) return null;
  const telegramAuthorOnly = Boolean(
    telegramHref &&
    !canLaunch &&
    !contact.raw
  );
  const baseClass = "inline-flex min-h-[46px] items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-black no-underline transition active:scale-[0.99]";

  const hubVariant = variant === "hub";

  const callClass = hubVariant
    ? "bg-blue-600 text-white hover:bg-blue-500"
    : "bg-emerald-600 text-white hover:bg-emerald-500";

  const whatsappClass = hubVariant
    ? "border border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700"
    : "border border-emerald-400/35 bg-emerald-950/60 text-emerald-100 hover:bg-emerald-900/70";

  const telegramClass = hubVariant
    ? "border border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700"
    : "border border-sky-400/35 bg-sky-950/60 text-sky-100 hover:bg-sky-900/70";

  return (
    <div
      className={`grid grid-cols-2 gap-2 ${className}`.trim()}
      data-reisor-contact-actions="v1"
      data-reisor-freight-draft={normalizedDraftText ? "v1" : undefined}
    >
      {canLaunch ? (
        <a
          href={`tel:${contact.tel}`}
          className={`${baseClass} ${callClass}`}
        >
          <Phone className="h-4 w-4" />
          <span>{callLabel || t("contactActions.call", { defaultValue: "Позвонить" })}</span>
        </a>
      ) : null}

      {canLaunch ? (
        <a
          href={contact.whatsapp}
          target="_blank"
          rel="noopener noreferrer"
          className={`${baseClass} ${whatsappClass}`}
        >
          <MessageCircle className="h-4 w-4" />
          <span>WhatsApp</span>
        </a>
      ) : null}

      {telegramHref ? (
        <a
          href={telegramHref}
          target={telegramHref.startsWith("https://") ? "_blank" : undefined}
          rel={telegramHref.startsWith("https://") ? "noopener noreferrer" : undefined}
          className={`${baseClass} ${telegramClass} ${!canLaunch && !contact.raw ? "col-span-2" : ""}`.trim()}
        >
          <Send className="h-4 w-4" />
          <span>
            {telegramAuthorOnly
              ? t("contactActions.telegramAuthor", {
                  defaultValue: "Telegram · Написать автору",
                })
              : "Telegram"}
          </span>
        </a>
      ) : null}

      {contact.raw ? (
        <button
          type="button"
          className={`${baseClass} border border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700 ${canLaunch ? "" : "col-span-2"}`.trim()}
          onClick={async () => {
            const ok = await copyText(contact.raw);
            setCopied(ok);
            setCopyFailed(!ok);
          }}
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          <span>
            {copied
              ? t("contactActions.copied", { defaultValue: "Скопировано" })
              : copyFailed
                ? t("contactActions.copyFailed", { defaultValue: "Не удалось скопировать" })
                : t("contactActions.copy", { defaultValue: "Копировать" })}
          </span>
        </button>
      ) : null}

    </div>
  );
}
